/**
 * Triangle Navigation Module
 * Handles the animated triangular navigation widget
 */

import gsap from 'gsap'
import TriangleRenderer from './TriangleRenderer.js'
import StateManager from './StateManager.js'
import AnimationController from './AnimationController.js'
import { STATES } from './constants.js'

export default class TriangleNav {
  constructor(app, opts = {}) {
    this.app = app
    this.opts = opts
    this.instances = []

    this.init()
  }

  init() {
    console.log('[TriangleNav] init() called at', Date.now())

    // Find all triangle nav widgets
    const widgets = document.querySelectorAll('[data-triangle-nav-widget]')
    console.log('[TriangleNav] Found', widgets.length, 'widget(s) in DOM')

    widgets.forEach((el, index) => {
      console.log(`[TriangleNav] Initializing widget ${index + 1}/${widgets.length}`)
      const instance = this.initializeWidget(el)
      if (instance) {
        this.instances.push(instance)
      }
    })

    console.log(`[TriangleNav] Initialized ${this.instances.length} widget(s)`)
  }

  initializeWidget(el) {
    // console.log('[TriangleNav] Initializing widget', el);

    const instance = {
      el,
      renderer: null,
      stateManager: null,
      animationController: null,
      data: null,
      isInitialized: false,
    }

    // Parse initial data from element
    const dataAttr = el.getAttribute('data-triangle-nav-data')
    if (dataAttr) {
      try {
        instance.data = JSON.parse(dataAttr)
      } catch (e) {
        console.error('[TriangleNav] Failed to parse data:', e)
        instance.data = {
          currentState: 'start',
          dimensions: [],
          areas: {},
        }
      }
    }

    // Initialize renderer
    instance.renderer = new TriangleRenderer(el)
    instance.renderer.init()

    // Initialize state manager
    instance.stateManager = new StateManager()
    instance.stateManager.init(instance.data)

    // Initialize animation controller
    instance.animationController = new AnimationController(instance.renderer, instance.data)

    // Check if we have a previous state from sessionStorage (for cross-page animations)
    const previousStateKey = 'triangleNav:previousState'
    const storedPreviousState = sessionStorage.getItem(previousStateKey)
    let shouldAnimate = false
    let previousState = null

    console.log('[TriangleNav] initializeWidget called, storedPreviousState:', storedPreviousState ? 'EXISTS' : 'NULL')
    console.log('[TriangleNav] Current time:', Date.now())

    if (storedPreviousState) {
      try {
        previousState = JSON.parse(storedPreviousState)
        const currentState = instance.stateManager.getState()

        // Check if we're transitioning between different states that should animate
        if (previousState.type === STATES.AREA && currentState.type === STATES.AREA) {
          // Area to Area transition - animate if dimension OR area changed
          shouldAnimate =
            previousState.dimension !== currentState.dimension ||
            previousState.area !== currentState.area
        } else if (previousState.type === STATES.DIMENSION && currentState.type === STATES.AREA) {
          // Dimension to Area transition - always animate
          shouldAnimate = true
        } else if (previousState.type === STATES.AREA && currentState.type === STATES.DIMENSION) {
          // Area to Dimension transition (clicking X) - always animate
          shouldAnimate = true
        } else if (
          previousState.type === STATES.START &&
          currentState.type === STATES.DIMENSION
        ) {
          // Start to Dimension - always animate
          shouldAnimate = true
        } else if (
          previousState.type === STATES.DIMENSION &&
          currentState.type === STATES.START
        ) {
          // Dimension to Start - always animate
          shouldAnimate = true
        }

        console.log('[TriangleNav] Previous state from storage:', previousState)
        console.log('[TriangleNav] Should animate initial load?', shouldAnimate)
      } catch (e) {
        console.error('[TriangleNav] Failed to parse previous state:', e)
      }
      // Clear the stored state
      sessionStorage.removeItem(previousStateKey)
    }

    // Render initial state (pass previousState for circle animations)
    const initialState = instance.stateManager.getState()
    instance.renderer.renderState(
      initialState,
      instance.data,
      shouldAnimate ? previousState : null
    )

    if (shouldAnimate && previousState) {
      // Set center circle to previous position BEFORE animating (with area offset)
      const previousCenterPosition = instance.renderer.getCenterPosition(
        previousState.dimension || 'start',
        previousState.area,
        instance.data
      )
      const centerCircles = instance.renderer.getCenterCircles()

      gsap.set(centerCircles, {
        attr: { cx: previousCenterPosition.cx, cy: previousCenterPosition.cy },
      })

      console.log(
        '[TriangleNav] Set center circles to previous position (area-specific):',
        previousCenterPosition
      )

      // Now animate from previous state to current state
      console.log('[TriangleNav] >>> PLAYING TRANSITION ANIMATION', { from: previousState, to: initialState })
      instance.animationController.animateTransition(initialState, previousState, 'forward')
    } else {
      // Check if this is first load (no previous state in session storage)
      const isFirstLoad = !storedPreviousState

      if (isFirstLoad) {
        // First page load - play entry animation
        console.log('[TriangleNav] >>> PLAYING ENTRY SEQUENCE (isFirstLoad=true, storedPreviousState was NULL)')
        instance.animationController.animateEntrySequence(initialState)
      } else {
        // Not first load and no transition needed - instant render
        console.log('[TriangleNav] >>> PLAYING INSTANT (shouldAnimate=false but had previousState)')
        instance.animationController.animateInstant(initialState)
      }
    }

    instance.animationController.setupHoverAnimations()

    // Listen for state changes
    instance.stateManager.onChange((newState, oldState, options) => {
      console.log('[TriangleNav] State change:', oldState, '->', newState)

      // Re-render circles
      instance.renderer.renderState(newState, instance.data)

      // Animate if requested
      if (options.animate) {
        const direction = instance.stateManager.getTransitionDirection()
        instance.animationController.animateTransition(newState, oldState, direction)
      } else {
        instance.animationController.animateInstant(newState)
      }

      // Re-setup hover animations after render
      setTimeout(() => {
        instance.animationController.setupHoverAnimations()
      }, 100)
    })

    // Handle triangle shape click (navigate up a level)
    const triangleShape = instance.renderer.elements.triangle
    if (triangleShape) {
      triangleShape.addEventListener('click', _e => {
        const currentState = instance.stateManager.getState()

        // Don't do anything if at start state
        if (currentState.type === STATES.START) {
          return
        }

        const hasViewTransitions = typeof document.startViewTransition === 'function'

        // Store current state for cross-page animation
        sessionStorage.setItem('triangleNav:previousState', JSON.stringify(currentState))

        let url = null
        let type = null

        if (currentState.type === STATES.AREA) {
          // Area -> Dimension: navigate to parent dimension
          const dimension = instance.data.dimensions?.find(
            d => d.handle === currentState.dimension
          )
          if (dimension?.url) {
            url = dimension.url
            type = 'dimension'
          }
        } else if (currentState.type === STATES.DIMENSION) {
          // Dimension -> Start: navigate to explore model page
          url = '/utforsk-modellen'
          type = 'start'
        }

        if (!url) return

        console.log('[TriangleNav] Triangle clicked - navigating up to:', url)

        if (hasViewTransitions) {
          window.dispatchEvent(
            new CustomEvent('triangle-nav-navigate', {
              detail: { url, handle: type, type, isBackNavigation: true },
            })
          )

          setTimeout(() => {
            if (!document.body.classList.contains('view-transitions-active')) {
              console.log('[TriangleNav] FALLBACK NAVIGATION TRIGGERED (triangle shape click)')
              window.location.href = url
            }
          }, 100)
        } else {
          console.log('[TriangleNav] NO VIEW TRANSITIONS - direct navigation (triangle shape)')
          window.location.href = url
        }
      })
    }

    // Handle circle clicks (for dimensions on start state)
    el.addEventListener('click', e => {
      const link = e.target.closest('a[data-handle]')
      if (!link) return

      // Check if View Transitions API is available
      const hasViewTransitions = typeof document.startViewTransition === 'function'

      // If not using view transitions, let the link work normally
      if (!hasViewTransitions) return

      // Prevent default navigation - we'll handle it via View Transitions
      e.preventDefault()

      const handle = link.getAttribute('data-handle')
      const type = link.getAttribute('data-type')
      const isActive = link.querySelector('circle[data-active="true"]') !== null
      const parentDimensionUrl = link.getAttribute('data-parent-dimension-url')
      let url = link.getAttribute('href')

      console.log('[TriangleNav] Circle clicked:', {
        handle,
        type,
        url,
        isActive,
        parentDimensionUrl,
      })

      // Update state based on type
      const currentState = instance.stateManager.getState()

      // Store current state in sessionStorage for cross-page animation
      sessionStorage.setItem('triangleNav:previousState', JSON.stringify(currentState))

      // If clicking on an active area circle, navigate back to parent dimension
      if (isActive && parentDimensionUrl && currentState.type === STATES.AREA) {
        console.log('[TriangleNav] Active circle clicked - navigating back to dimension')
        url = parentDimensionUrl
      }
      // Note: Don't call goToDimension/goToArea here - we're navigating away
      // and the new page will handle the animation after load.
      // The previousState is already stored in sessionStorage above.

      // Trigger navigation via View Transitions
      // The ViewTransitions module will handle the actual page load
      if (url) {
        // Dispatch custom event for ViewTransitions module to pick up
        window.dispatchEvent(
          new CustomEvent('triangle-nav-navigate', {
            detail: { url, handle, type, isBackNavigation: isActive },
          })
        )

        // If ViewTransitions module doesn't exist, fallback to regular navigation
        setTimeout(() => {
          if (!document.body.classList.contains('view-transitions-active')) {
            console.log('[TriangleNav] FALLBACK NAVIGATION TRIGGERED (circle click)')
            window.location.href = url
          }
        }, 100)
      }
    })

    // Expose update method for ViewTransitions integration
    instance.updateState = newData => {
      console.log('[TriangleNav] Updating state with new data:', newData)
      instance.data = newData
      instance.stateManager.updateData(newData)
    }

    // Store instance globally for ViewTransitions access
    if (!window.triangleNavInstances) {
      window.triangleNavInstances = []
    }
    window.triangleNavInstances.push(instance)

    // Setup navigation control buttons
    this.setupNavigationControls(instance)

    instance.isInitialized = true
    return instance
  }

  setupNavigationControls(instance) {
    // Find the container
    const container = instance.el.closest('.triangle-nav-container')
    if (!container) {
      console.warn('[TriangleNav] Container not found, skipping navigation controls setup')
      return
    }

    // Find control buttons
    const backButton = container.querySelector('[data-nav-control="back-to-start"]')
    const tabSwitcher = container.querySelector('[data-nav-control="tab-switcher"]')
    const closeAreaButton = container.querySelector('[data-nav-control="close-area"]')
    const triangleView = container.querySelector('[data-view="omrader"]')
    const topicsView = container.querySelector('[data-view="temaer"]')

    // Tab switcher functionality
    if (tabSwitcher) {
      const omraderTab = tabSwitcher.querySelector('[data-tab="omrader"]')
      const temaerTab = tabSwitcher.querySelector('[data-tab="temaer"]')

      omraderTab?.addEventListener('click', () => {
        // Switch to areas view
        omraderTab.classList.add('is-active')
        omraderTab.setAttribute('aria-pressed', 'true')
        temaerTab.classList.remove('is-active')
        temaerTab.setAttribute('aria-pressed', 'false')

        // Toggle views
        triangleView?.classList.remove('is-hidden')
        topicsView?.classList.add('is-hidden')

        // Remeasure tooltips now that SVG is visible (getBBox needs visible elements)
        instance.renderer?.remeasureTooltips()

        // Clear search state when leaving topics view
        this.clearSearchState()

        console.log('[TriangleNav] Switched to Områder view')
      })

      temaerTab?.addEventListener('click', () => {
        // Switch to topics view
        temaerTab.classList.add('is-active')
        temaerTab.setAttribute('aria-pressed', 'true')
        omraderTab.classList.remove('is-active')
        omraderTab.setAttribute('aria-pressed', 'false')

        // Toggle views
        triangleView?.classList.add('is-hidden')
        topicsView?.classList.remove('is-hidden')

        console.log('[TriangleNav] Switched to Temaer view')

        // Load topics if not already loaded
        this.loadTopics(topicsView)
      })

      // Auto-load topics if we have a search query to restore
      if (topicsView?.dataset.restoreQuery) {
        console.log('[TriangleNav] Auto-loading topics for search restoration')
        this.loadTopics(topicsView)
      }
    }

    // Back to start button
    if (backButton) {
      backButton.addEventListener('click', () => {
        console.log('[TriangleNav] Back to start clicked')

        const currentState = instance.stateManager.getState()

        // Check if View Transitions API is available
        const hasViewTransitions = typeof document.startViewTransition === 'function'

        // Store current state for cross-page animation
        sessionStorage.setItem('triangleNav:previousState', JSON.stringify(currentState))

        // Find the start page URL (exploreModel)
        const startUrl = '/utforsk-modellen'

        if (hasViewTransitions) {
          // Dispatch custom event for ViewTransitions module
          window.dispatchEvent(
            new CustomEvent('triangle-nav-navigate', {
              detail: { url: startUrl, handle: 'start', type: 'start', isBackNavigation: true },
            })
          )

          // Fallback if ViewTransitions module doesn't exist
          setTimeout(() => {
            if (!document.body.classList.contains('view-transitions-active')) {
              console.log('[TriangleNav] FALLBACK NAVIGATION TRIGGERED (back to start)')
              window.location.href = startUrl
            }
          }, 100)
        } else {
          console.log('[TriangleNav] NO VIEW TRANSITIONS - direct navigation (back to start)')
          window.location.href = startUrl
        }
      })
    }

    // Close area button
    if (closeAreaButton) {
      closeAreaButton.addEventListener('click', () => {
        console.log('[TriangleNav] Close area clicked')

        const parentUrl = closeAreaButton.getAttribute('data-parent-url')
        if (!parentUrl) {
          console.warn('[TriangleNav] No parent URL found for close area button')
          return
        }

        const currentState = instance.stateManager.getState()

        // Check if View Transitions API is available
        const hasViewTransitions = typeof document.startViewTransition === 'function'

        // Store current state for cross-page animation
        sessionStorage.setItem('triangleNav:previousState', JSON.stringify(currentState))

        if (hasViewTransitions) {
          // Dispatch custom event for ViewTransitions module
          window.dispatchEvent(
            new CustomEvent('triangle-nav-navigate', {
              detail: {
                url: parentUrl,
                handle: currentState.dimension,
                type: 'dimension',
                isBackNavigation: true,
              },
            })
          )

          // Fallback if ViewTransitions module doesn't exist
          setTimeout(() => {
            if (!document.body.classList.contains('view-transitions-active')) {
              console.log('[TriangleNav] FALLBACK NAVIGATION TRIGGERED (close area)')
              window.location.href = parentUrl
            }
          }, 100)
        } else {
          console.log('[TriangleNav] NO VIEW TRANSITIONS - direct navigation (close area)')
          window.location.href = parentUrl
        }
      })
    }

    // Setup overflow arrow for tall aside
    this.setupOverflowArrow(container)

    console.log('[TriangleNav] Navigation controls setup complete')
  }

  loadTopics(topicsView) {
    if (!topicsView) return

    // Check if already loaded
    if (topicsView.dataset.topicsLoaded === 'true') {
      console.log('[TriangleNav] Topics already loaded')
      return
    }

    const container = topicsView.querySelector('[data-topics-container]')
    if (!container) {
      console.warn('[TriangleNav] Topics container not found')
      return
    }

    // Show loading state
    container.innerHTML = '<p style="text-align: center; padding: 20px;">Laster temaer...</p>'

    // Fetch topics from AJAX endpoint
    fetch('/_ajax/topics')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(topics => {
        console.log('[TriangleNav] Topics loaded:', topics)

        // Clear loading state
        container.innerHTML = ''

        // Store all topics for filtering
        topicsView.dataset.allTopics = JSON.stringify(topics)

        // Check if we need to restore search state
        if (topicsView.dataset.restoreQuery) {
          // Restore search state (will render search results instead of topics)
          this.restoreSearchState(topicsView)
        } else {
          // Render default topics
          this.renderTopics(topics, container)
        }

        // Setup interactions (search, expand/collapse, clicks)
        this.setupTopicsInteractions(topicsView)

        // Mark as loaded
        topicsView.dataset.topicsLoaded = 'true'
      })
      .catch(error => {
        console.error('[TriangleNav] Failed to load topics:', error)
        container.innerHTML =
          '<p style="text-align: center; padding: 20px; color: red;">Kunne ikke laste temaer. Vennligst prøv igjen.</p>'
      })
  }

  renderTopics(topics, container) {
    // Clear existing content first
    container.innerHTML = ''

    if (!topics || topics.length === 0) {
      container.innerHTML =
        '<p style="text-align: center; padding: 20px;">Ingen temaer funnet.</p>'
      return
    }

    // Create topic buttons
    const fragment = document.createDocumentFragment()

    topics.forEach(topic => {
      const button = document.createElement('a')
      button.className = 'topic-button'
      button.href = topic.areaUrl
      button.textContent = topic.title
      button.dataset.topicTitle = topic.title.toLowerCase()

      // Clear search state when clicking regular topic tags
      button.addEventListener('click', () => {
        this.clearSearchState()
      })

      fragment.appendChild(button)
    })

    container.appendChild(fragment)
  }

  setupTopicsInteractions(topicsView) {
    const searchInput = topicsView.querySelector('[data-topics-search]')
    const searchButton = topicsView.querySelector('[data-topics-search-button]')
    const container = topicsView.querySelector('[data-topics-container]')
    const wrapper = topicsView.querySelector('[data-topics-wrapper]')
    const expandButton = topicsView.querySelector('[data-topics-expand]')
    const expandText = expandButton?.querySelector('[data-expand-text]')

    if (!searchInput || !container || !wrapper || !expandButton) {
      console.warn('[TriangleNav] Topics interaction elements not found')
      return
    }

    // Get all topics from data attribute
    let allTopics = []
    try {
      allTopics = JSON.parse(topicsView.dataset.allTopics || '[]')
    } catch (e) {
      console.error('[TriangleNav] Failed to parse topics data:', e)
    }

    // Search on Enter key
    searchInput.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.performSearch(searchInput.value, topicsView, allTopics)
      }
    })

    // Search on button click
    if (searchButton) {
      searchButton.addEventListener('click', () => {
        this.performSearch(searchInput.value, topicsView, allTopics)
      })
    }

    // Clear search on empty input (reset to tags view)
    searchInput.addEventListener('input', e => {
      if (e.target.value.trim() === '') {
        topicsView.classList.remove('is-search-mode')
        this.renderTopics(allTopics, container)
        this.clearSearchState()
      }
    })

    // Expand/collapse functionality
    let isExpanded = false

    const expandTopics = () => {
      if (!isExpanded) {
        isExpanded = true
        wrapper.classList.add('is-expanded')
        expandButton.setAttribute('aria-expanded', 'true')
        if (expandText) expandText.textContent = 'Vis færre'
      }
    }

    expandButton.addEventListener('click', () => {
      isExpanded = !isExpanded

      if (isExpanded) {
        wrapper.classList.add('is-expanded')
        expandButton.setAttribute('aria-expanded', 'true')
        if (expandText) expandText.textContent = 'Vis færre'
      } else {
        wrapper.classList.remove('is-expanded')
        expandButton.setAttribute('aria-expanded', 'false')
        if (expandText) expandText.textContent = 'Vis flere'
      }
    })

    // Auto-expand when tabbing into topics (user intent)
    container.addEventListener('focusin', expandTopics)

    console.log('[TriangleNav] Topics interactions setup complete')
  }

  performSearch(searchTerm, topicsView, allTopics) {
    const query = searchTerm.trim()
    const container = topicsView.querySelector('[data-topics-container]')
    const wrapper = topicsView.querySelector('[data-topics-wrapper]')
    const expandButton = topicsView.querySelector('[data-topics-expand]')
    const expandText = expandButton?.querySelector('[data-expand-text]')

    // If empty, reset to default view
    if (query === '') {
      topicsView.classList.remove('is-search-mode')
      this.renderTopics(allTopics, container)
      return
    }

    // Require at least 2 characters
    if (query.length < 2) {
      return
    }

    // Show loading state
    container.innerHTML = '<p class="search-loading">Søker...</p>'

    // Fetch search results from server
    fetch(`/_ajax/topics?q=${encodeURIComponent(query)}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then(results => {
        topicsView.classList.add('is-search-mode')

        // Store search state BEFORE rendering (so links get ?q= parameter)
        sessionStorage.setItem('triangleNav:searchQuery', query)
        sessionStorage.setItem('triangleNav:searchResults', JSON.stringify(results))

        this.renderSearchResults(results, container)

        // Auto-expand to show results
        wrapper.classList.add('is-expanded')
        expandButton.setAttribute('aria-expanded', 'true')
        if (expandText) expandText.textContent = 'Vis færre'
      })
      .catch(error => {
        console.error('[TriangleNav] Search failed:', error)
        container.innerHTML = '<p class="search-no-results">Søket feilet. Vennligst prøv igjen.</p>'
      })
  }

  renderSearchResults(results, container) {
    container.innerHTML = ''

    if (!results || results.length === 0) {
      container.innerHTML = '<p class="search-no-results">Ingen treff</p>'
      return
    }

    const fragment = document.createDocumentFragment()
    const currentPath = window.location.pathname
    const currentQuery = sessionStorage.getItem('triangleNav:searchQuery') || ''

    // Add result count
    const countText = document.createElement('p')
    countText.className = 'search-result-count'
    countText.textContent = `${results.length} treff`
    fragment.appendChild(countText)

    results.forEach(result => {
      const item = document.createElement('a')
      item.className = 'search-result'
      item.dataset.searchResult = 'true'

      // Add ?q= parameter to preserve search state across navigation
      const targetUrl = new URL(result.areaUrl, window.location.origin)
      if (currentQuery) {
        targetUrl.searchParams.set('q', currentQuery)
      }
      item.href = targetUrl.toString()

      item.innerHTML = `
        <div class="result-breadcrumb">
          ${result.dimensionTitle ? `<span class="result-dimension">${this.escapeHtml(result.dimensionTitle)}</span><span class="result-separator">→</span>` : ''}
          <span class="result-area">${this.escapeHtml(result.areaTitle)}</span>
          <span class="result-separator">→</span>
          <span class="result-tag">${this.escapeHtml(result.title)}</span>
        </div>
        <p class="result-excerpt">${result.excerpt}</p>
      `

      // Handle same-page navigation
      item.addEventListener('click', e => {
        // Save scroll position before navigating
        const wrapper = container.closest('[data-topics-wrapper]')
        if (wrapper) {
          sessionStorage.setItem('triangleNav:scrollPosition', wrapper.scrollTop)
        }

        if (targetUrl.pathname === currentPath) {
          e.preventDefault()
          this.scrollToTopic(result.slug)
        }
        // For different page navigation, the ?q in href will preserve state
      })

      fragment.appendChild(item)
    })

    container.appendChild(fragment)
  }

  scrollToTopic(slug) {
    // Find the topic toggle element
    const topicElement = document.querySelector(`[data-topic="${slug}"]`)
    if (!topicElement) {
      // Fallback: try finding by ID
      const byId = document.getElementById(slug)
      if (byId) {
        byId.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Open the toggle if it exists
        const trigger = byId.querySelector('[data-toggle-trigger]')
        if (trigger && !byId.classList.contains('open')) {
          trigger.click()
        }
      }
      return
    }

    // Scroll to the topic
    topicElement.scrollIntoView({ behavior: 'smooth', block: 'start' })

    // Open the toggle if it's not already open
    const trigger = topicElement.querySelector('[data-toggle-trigger]')
    if (trigger && !topicElement.classList.contains('open')) {
      trigger.click()
    }

    // Update URL hash
    history.pushState(null, '', `#tema:${slug}`)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  setupOverflowArrow(container) {
    // Don't run on mobile
    if (window.innerWidth <= 768) return

    const aside = container.closest('aside')
    if (!aside) return

    // Create arrow button with down chevron SVG
    const arrow = document.createElement('button')
    arrow.className = 'aside-scroll-arrow'
    arrow.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
    arrow.setAttribute('aria-label', 'Vis mer av navigasjonen')
    document.body.appendChild(arrow)

    let isShifted = false
    let overflowAmount = 0

    const checkOverflow = () => {
      // Don't re-measure when already shifted - causes visual bounce
      if (isShifted) return

      const asideRect = aside.getBoundingClientRect()
      const viewportBottom = window.innerHeight
      const navbarHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--navbar-height') || '86',
        10
      )
      overflowAmount = asideRect.bottom - viewportBottom

      // Check if aside is at its sticky position (top near navbar height)
      const isStuck = asideRect.top <= navbarHeight + 5

      // Show arrow only if content overflows AND aside is stuck at top
      if (overflowAmount > 0 && isStuck) {
        arrow.classList.add('is-visible')
      } else {
        arrow.classList.remove('is-visible')
      }
    }

    arrow.addEventListener('click', () => {
      if (!isShifted) {
        // Shift content up to reveal bottom
        container.style.transform = `translateY(-${overflowAmount}px)`
        arrow.classList.add('is-flipped')
        arrow.setAttribute('aria-label', 'Vis toppen av navigasjonen')
        isShifted = true
      } else {
        // Reset to top
        container.style.transform = 'translateY(0)'
        arrow.classList.remove('is-flipped')
        arrow.setAttribute('aria-label', 'Vis mer av navigasjonen')
        isShifted = false
        // Re-check overflow after reset
        setTimeout(checkOverflow, 50)
      }
    })

    // Auto-reset when page scrolls to top
    let scrollTimeout
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        if (window.scrollY === 0 && isShifted) {
          container.style.transform = 'translateY(0)'
          arrow.classList.remove('is-flipped')
          arrow.setAttribute('aria-label', 'Vis mer av navigasjonen')
          isShifted = false
        }
        checkOverflow()
      }, 50)
    })

    // Initial check after a short delay (let layout settle)
    setTimeout(checkOverflow, 100)
    window.addEventListener('resize', () => {
      // Hide on mobile after resize
      if (window.innerWidth <= 768) {
        arrow.classList.remove('is-visible')
        return
      }
      checkOverflow()
    })

    console.log('[TriangleNav] Overflow arrow setup complete')
  }

  /**
   * Clear search state from URL and sessionStorage
   */
  clearSearchState() {
    // Remove ?q from URL
    const url = new URL(window.location.href)
    if (url.searchParams.has('q')) {
      url.searchParams.delete('q')
      history.replaceState(null, '', url.toString())
    }

    // Clear sessionStorage
    sessionStorage.removeItem('triangleNav:searchQuery')
    sessionStorage.removeItem('triangleNav:searchResults')
    sessionStorage.removeItem('triangleNav:scrollPosition')
  }

  /**
   * Restore search state from URL/sessionStorage on page load
   * @returns {boolean} Whether search state was restored
   */
  restoreSearchState(topicsView) {
    const restoreQuery = topicsView.dataset.restoreQuery
    if (!restoreQuery) return false

    const searchInput = topicsView.querySelector('[data-topics-search]')
    const container = topicsView.querySelector('[data-topics-container]')
    const wrapper = topicsView.querySelector('[data-topics-wrapper]')
    const expandButton = topicsView.querySelector('[data-topics-expand]')
    const expandText = expandButton?.querySelector('[data-expand-text]')

    // Set input value
    searchInput.value = restoreQuery

    // Check sessionStorage for cached results
    const cachedQuery = sessionStorage.getItem('triangleNav:searchQuery')
    const cachedResults = sessionStorage.getItem('triangleNav:searchResults')

    // Helper to restore scroll position after render
    const restoreScrollPosition = () => {
      const savedScrollPosition = sessionStorage.getItem('triangleNav:scrollPosition')
      if (savedScrollPosition && wrapper) {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          wrapper.scrollTop = parseInt(savedScrollPosition, 10)
        })
      }
    }

    if (cachedQuery === restoreQuery && cachedResults) {
      // Restore from cache - faster, no network request
      topicsView.classList.add('is-search-mode')
      this.renderSearchResults(JSON.parse(cachedResults), container)

      // Auto-expand
      wrapper?.classList.add('is-expanded')
      expandButton?.setAttribute('aria-expanded', 'true')
      if (expandText) expandText.textContent = 'Vis færre'

      // Restore scroll position
      restoreScrollPosition()

      console.log('[TriangleNav] Search state restored from cache')
      return true
    }

    // Cache miss - need to re-fetch
    console.log('[TriangleNav] Cache miss, re-fetching search results')
    fetch(`/_ajax/topics?q=${encodeURIComponent(restoreQuery)}`)
      .then(r => r.json())
      .then(results => {
        topicsView.classList.add('is-search-mode')
        this.renderSearchResults(results, container)

        // Store in sessionStorage
        sessionStorage.setItem('triangleNav:searchQuery', restoreQuery)
        sessionStorage.setItem('triangleNav:searchResults', JSON.stringify(results))

        // Auto-expand
        wrapper?.classList.add('is-expanded')
        expandButton?.setAttribute('aria-expanded', 'true')
        if (expandText) expandText.textContent = 'Vis færre'

        // Restore scroll position
        restoreScrollPosition()
      })
      .catch(error => {
        console.error('[TriangleNav] Failed to restore search:', error)
        container.innerHTML = '<p class="search-no-results">Kunne ikke gjenopprette søket.</p>'
      })

    return true
  }

  destroy() {
    this.instances.forEach(instance => {
      if (instance.animationController) {
        instance.animationController.destroy()
      }
    })

    // Clean up global reference
    delete window.triangleNavInstances
  }
}
